import {
  FIVE9_CALL_API,
  FIVE9_CALL_QUEUE_VERSION,
  five9CallPath,
  five9TranscriptPath
} from 'src/constants/five9CallApi';
import { isRetryableFive9CallError } from 'src/services/five9CallsService';
import five9Logger from './five9Logger';
import { mapFive9CallForReconciliation } from './mapFive9CallEvent';
import { five9CallQueueStorageKeyFor } from './five9CallQueueStorage';

const RECENT_SIGNATURE_LIMIT = 100;
// A retryable failure (timeout or HTTP 503) must not sit in the
// queue forever waiting for a new lifecycle event or reconciliation to
// happen to redrain it. Schedule an independent backoff-scheduled retry.
const QUEUE_RETRY_INITIAL_DELAY_MS = 1000;
const QUEUE_RETRY_MAX_DELAY_MS = 30000;
const RECONCILIATION_RETRY_INITIAL_DELAY_MS = 1000;
const RECONCILIATION_RETRY_MAX_DELAY_MS = 30000;
const RECONCILIATION_MAX_ATTEMPTS = 8;
// Retries must be bounded. Without a cap, an unreachable API turns the
// head of the queue into an infinite publish loop that also blocks every
// later event behind it (call-end, disposition) forever. Give up on an
// individual operation after enough attempts or once it is stale enough
// that the call it describes is long over, and log loudly when we do.
const QUEUE_MAX_ATTEMPTS = 8;
const QUEUE_MAX_AGE_MS = 6 * 60 * 60 * 1000;

const hashString = (value) => {
  let hash = 5381;
  for (let i = 0; i < value.length; i += 1) {
    hash = (hash * 33 + value.charCodeAt(i)) % 2147483647;
  }
  return hash.toString(36);
};

const signatureFor = (operation) => hashString(JSON.stringify([
  operation.method,
  operation.path,
  operation.body
]));

// The queue is loaded from sessionStorage, which is writable by any script
// on the same origin and can theoretically be corrupted or tampered with.
// `operation.path` is passed straight into the axios client (see
// `five9CallsService.js`), so an absolute or protocol-relative path could
// redirect an authenticated request (with auth headers) to an
// attacker-controlled origin. Only the three exact request shapes this
// queue ever produces (see `mapFive9CallEvent.js`) are allowed through;
// everything else is dropped before it ever reaches the HTTP client.
const VALID_QUEUE_METHODS = new Set(['POST', 'PATCH', 'PUT']);
const CALLS_PATH_PREFIX = `${FIVE9_CALL_API.callsPath}/`;
const TRANSCRIPT_PATH_SUFFIX = '/transcript';
const LIFECYCLE_FIELDS = [
  FIVE9_CALL_API.agentIdField,
  FIVE9_CALL_API.aniField,
  FIVE9_CALL_API.dnisField,
  FIVE9_CALL_API.queueField,
  FIVE9_CALL_API.campaignField,
  FIVE9_CALL_API.outcomeField,
  FIVE9_CALL_API.recordingUrlField,
  FIVE9_CALL_API.callStateField,
  FIVE9_CALL_API.callStartedAtField,
  FIVE9_CALL_API.callAnsweredAtField,
  FIVE9_CALL_API.callEndedAtField,
  FIVE9_CALL_API.talkTimeSecondsField,
  FIVE9_CALL_API.dispositionField
];
const POST_BODY_FIELDS = new Set([
  FIVE9_CALL_API.sessionIdField,
  FIVE9_CALL_API.interactionIdField,
  FIVE9_CALL_API.callIdField,
  FIVE9_CALL_API.directionField,
  ...LIFECYCLE_FIELDS
]);
const PATCH_BODY_FIELDS = new Set([
  FIVE9_CALL_API.interactionIdField,
  FIVE9_CALL_API.callIdField,
  ...LIFECYCLE_FIELDS
]);
const TRANSCRIPT_BODY_FIELDS = new Set([
  FIVE9_CALL_API.transcriptField,
  FIVE9_CALL_API.summaryField,
  FIVE9_CALL_API.transcriptReceivedAtField
]);

const isSafePathSegment = (segment) => (
  typeof segment === 'string'
  && segment.length > 0
  && !/[/\\]/.test(segment)
);

const isValidOperationPath = (method, path) => {
  if (typeof path !== 'string' || !path.length) return false;
  // Reject absolute URLs, protocol-relative URLs, and any leading slash —
  // legitimate paths are always relative (`calls`, `calls/{id}`, …).
  if (/^[a-z][a-z0-9+.-]*:/i.test(path) || path.startsWith('/')) return false;
  if (method === 'POST') return path === FIVE9_CALL_API.callsPath;
  if (!path.startsWith(CALLS_PATH_PREFIX)) return false;
  const rest = path.slice(CALLS_PATH_PREFIX.length);
  try {
    // Decode BEFORE checking for embedded slashes/backslashes. Checking the
    // still-encoded segment lets `%2F`/`%5C` sequences pass here (they
    // contain no literal `/`/`\`), even though some proxies/backends decode
    // the path before route matching — which would let a tampered segment
    // like `SESSION%2Ftranscript` on a PATCH resolve to a different route
    // than this validator intended. The round-trip re-encode below then
    // additionally guarantees the decoded value re-canonicalizes back to
    // this exact operation path.
    if (method === 'PATCH') {
      const segment = decodeURIComponent(rest);
      return isSafePathSegment(segment) && five9CallPath(segment) === path;
    }
    if (method === 'PUT') {
      if (!rest.endsWith(TRANSCRIPT_PATH_SUFFIX)) return false;
      const segment = decodeURIComponent(rest.slice(0, -TRANSCRIPT_PATH_SUFFIX.length));
      return isSafePathSegment(segment) && five9TranscriptPath(segment) === path;
    }
  } catch (_) {
    // Malformed percent-encoding in a tampered/corrupted entry.
    return false;
  }
  return false;
};

const isPlainObject = (value) => (
  typeof value === 'object'
  && value !== null
  && !Array.isArray(value)
  && Object.getPrototypeOf(value) === Object.prototype
);

// Extracts the five9SessionId a queued operation is about, so the
// reconciliation barrier (see `drain()`) can be scoped to just that
// session rather than freezing the entire queue. POST carries it in the
// body; PATCH/PUT carry it in the path. Returns null (never blocked) for
// anything that doesn't cleanly resolve, rather than guessing.
const sessionIdForOperation = (operation) => {
  if (!operation || typeof operation.path !== 'string') return null;
  if (operation.method === 'POST') {
    const sessionId = operation.body?.[FIVE9_CALL_API.sessionIdField];
    return sessionId != null ? String(sessionId) : null;
  }
  if (!operation.path.startsWith(CALLS_PATH_PREFIX)) return null;
  const rest = operation.path.slice(CALLS_PATH_PREFIX.length);
  const segment = operation.method === 'PUT' && rest.endsWith(TRANSCRIPT_PATH_SUFFIX)
    ? rest.slice(0, -TRANSCRIPT_PATH_SUFFIX.length)
    : rest;
  try {
    const decoded = decodeURIComponent(segment);
    return decoded || null;
  } catch (_) {
    return null;
  }
};

// Every real operation body is `compact()`-ed to a flat map of
// strings/numbers/booleans before being queued (see mapFive9CallEvent.js).
// The persisted queue is untrusted, so enforce both this shape and the
// operation-specific API contract. Field sets deliberately derive from
// FIVE9_CALL_API to stay aligned with the mapper without accepting arbitrary
// authenticated request data.
const hasRequiredFields = (body, fields) => fields.every((field) => (
  typeof body[field] === 'string' && body[field].length > 0
));

const isValidOperationBody = (method, path, body) => {
  if (!isPlainObject(body)
    || !Object.values(body).every((value) => (
      value === null || ['string', 'number', 'boolean'].includes(typeof value)
    ))) {
    return false;
  }
  let allowedFields;
  let requiredFields;
  if (method === 'POST' && path === FIVE9_CALL_API.callsPath) {
    allowedFields = POST_BODY_FIELDS;
    requiredFields = [
      FIVE9_CALL_API.sessionIdField,
      FIVE9_CALL_API.interactionIdField,
      FIVE9_CALL_API.callIdField,
      FIVE9_CALL_API.callStateField,
      FIVE9_CALL_API.callStartedAtField,
      FIVE9_CALL_API.callAnsweredAtField
    ];
  } else if (method === 'PATCH') {
    allowedFields = PATCH_BODY_FIELDS;
    requiredFields = [
      FIVE9_CALL_API.interactionIdField,
      FIVE9_CALL_API.callIdField,
      FIVE9_CALL_API.callStateField
    ];
  } else if (method === 'PUT' && path.endsWith(TRANSCRIPT_PATH_SUFFIX)) {
    allowedFields = TRANSCRIPT_BODY_FIELDS;
    requiredFields = [FIVE9_CALL_API.transcriptField];
  } else {
    return false;
  }
  return Object.keys(body).every((field) => allowedFields.has(field))
    && hasRequiredFields(body, requiredFields);
};

const isValidQueuedOperation = (operation) => (
  Boolean(operation)
  && typeof operation === 'object'
  && VALID_QUEUE_METHODS.has(operation.method)
  && isValidOperationPath(operation.method, operation.path)
  && isValidOperationBody(operation.method, operation.path, operation.body)
);

const emptyState = () => ({
  version: FIVE9_CALL_QUEUE_VERSION,
  queue: [],
  recent: []
});

const readState = (storage, key, nowMs) => {
  const purgeStorage = (reason) => {
    // Corrupted/incompatible persisted state is never safe to leave in
    // place: it would otherwise fail this same check (and repeat this
    // exact log) on every subsequent load, and can retain stale/tampered
    // PII-adjacent fields (ANI/DNIS/transcript/summary) indefinitely.
    try {
      storage?.setItem(key, JSON.stringify(emptyState()));
    } catch (_) {
      // Best effort — if we can't write, we also can't have persisted the
      // bad state further; nothing more to do.
    }
    five9Logger.warn('five9.callQueueStoragePurged', { reason });
  };
  try {
    const parsed = JSON.parse(storage?.getItem(key) || 'null');
    if (parsed?.version !== FIVE9_CALL_QUEUE_VERSION
      || !Array.isArray(parsed.queue)
      || !Array.isArray(parsed.recent)) {
      if (storage?.getItem(key)) purgeStorage('invalidShape');
      return { state: emptyState(), pruned: false, invalidDropped: 0 };
    }
    const originalQueueLength = parsed.queue.length;
    const originalRecentLength = parsed.recent.length;
    // Drop operations that were persisted long enough ago that the call
    // they describe is over. Replaying them on every page load is what
    // turns one unreachable-API window into a permanent publish loop.
    parsed.queue = parsed.queue.filter((item) => (
      typeof item?.enqueuedAt !== 'number' || (nowMs - item.enqueuedAt) < QUEUE_MAX_AGE_MS
    ));
    const beforeValidationLength = parsed.queue.length;
    // sessionStorage is writable by any script on this origin and can be
    // corrupted/tampered with. Validate every queued operation's shape at
    // load time — see `isValidQueuedOperation` above.
    parsed.queue = parsed.queue.filter((item) => isValidQueuedOperation(item?.operation));
    const invalidDropped = beforeValidationLength - parsed.queue.length;
    // Legacy builds stored the full operation JSON in `recent`, which could
    // retain transcript/summary after delivery. Current signatures are hashes;
    // drop old JSON-array signatures during load so cleanup is durable.
    parsed.recent = parsed.recent.filter((item) => (
      typeof item === 'string' && !item.startsWith('[')
    ));
    return {
      state: parsed,
      pruned: parsed.queue.length !== originalQueueLength
        || parsed.recent.length !== originalRecentLength,
      invalidDropped
    };
  } catch (_) {
    // JSON.parse failure means the persisted value is corrupted (or was
    // tampered with) beyond structural validation. Purge it immediately —
    // previously this only returned an in-memory emptyState(), leaving the
    // corrupted (possibly PII-bearing) value in storage to be re-parsed
    // (and fail identically) on every subsequent load.
    purgeStorage('parseFailure');
    return { state: emptyState(), pruned: false, invalidDropped: 0 };
  }
};

const createFive9CallQueue = ({
  identity,
  storage = typeof sessionStorage === 'undefined' ? null : sessionStorage,
  service,
  enabled = true,
  now = () => Date.now()
}) => {
  if (!identity) throw new Error('Authenticated identity is required for the Five9 call queue.');
  if (!service) throw new Error('Five9 calls service is required.');

  const key = five9CallQueueStorageKeyFor(identity);
  const { state, pruned: prunedOnLoad, invalidDropped } = readState(storage, key, now());
  const loadedPendingCount = state.queue.length;
  let isEnabled = enabled;
  let draining = null;
  let retryTimer = null;
  let retryDelayMs = QUEUE_RETRY_INITIAL_DELAY_MS;
  let disposed = false;
  let itemSequence = state.queue.reduce((highest, item) => {
    const suffix = Number(String(item?.id || '').split('-').pop());
    return Number.isFinite(suffix) ? Math.max(highest, suffix) : highest;
  }, 0);
  let reconciliationSequence = 0;
  let reconciliationRunning = null;
  let reconciliationRetryTimer = null;
  let reconciliationRetryDelayMs = RECONCILIATION_RETRY_INITIAL_DELAY_MS;
  const reconciliationPending = new Map();

  const clearTimer = (timer) => {
    if (timer) clearTimeout(timer);
    return null;
  };

  const persist = () => {
    try {
      storage?.setItem(key, JSON.stringify(state));
    } catch (_) {
      // Persistence failures must not block Five9 callbacks.
    }
  };
  if (prunedOnLoad) persist();
  if (invalidDropped) {
    five9Logger.warn('five9.callQueueInvalidEntriesDropped', {
      count: invalidDropped,
      reason: 'invalidOperationShape',
      stage: 'load'
    });
  }

  const remember = (signature) => {
    state.recent = [...state.recent.filter((item) => item !== signature), signature]
      .slice(-RECENT_SIGNATURE_LIMIT);
  };

  const removeItem = (item, rememberSignature = true) => {
    const index = state.queue.findIndex((candidate) => candidate.id === item.id);
    if (index === -1) return false;
    state.queue.splice(index, 1);
    if (rememberSignature) remember(item.signature);
    persist();
    return true;
  };

  let drain;
  let redrainRequested = false;

  const scheduleRetry = () => {
    if (retryTimer || disposed || !isEnabled || !state.queue.length) return;
    retryTimer = setTimeout(() => {
      retryTimer = null;
      drain();
    }, retryDelayMs);
    retryDelayMs = Math.min(retryDelayMs * 2, QUEUE_RETRY_MAX_DELAY_MS);
  };

  drain = () => {
    if (disposed || !isEnabled || draining || !state.queue.length) {
      // A drain() already in flight decided queue eligibility from a
      // snapshot at its own call time (see `processNext`'s single
      // `findIndex` scan below). If new work arrives — e.g. an unrelated
      // session's operation enqueued in the same tick, while this call's
      // only item was reconciliation-held — it must not go unprocessed
      // just because it missed that scan. Flag a redrain so the in-flight
      // call's `.finally()` re-invokes drain() once it settles.
      if (draining) redrainRequested = true;
      return draining || Promise.resolve();
    }
    // A scheduled retry is authoritative while it is pending. Callers like
    // reconcile() run on every Five9 state change; letting them clear the
    // timer and republish immediately collapses the backoff into a tight
    // loop against an API that is already failing.
    if (retryTimer) return Promise.resolve();
    // Reconciliation is the read-before-republish barrier after reload or
    // ownership regain, but it must only hold back the SPECIFIC session(s)
    // still being reconciled — otherwise one session's retrying GET (e.g.
    // during a partial outage) freezes every other session's unrelated
    // queued PATCH/PUT work too. `reconciliationPending` is already keyed
    // by five9SessionId (see `reconcile()`/`runReconciliation()`), so use
    // it directly per item via `sessionIdForOperation`. A session's hold is
    // released once its GET resolves conclusively (200/404) or the bounded
    // reconciliation retry cap is reached — see `runReconciliation()`.
    const isSessionHeld = (operation) => {
      const sessionId = sessionIdForOperation(operation);
      return sessionId != null && reconciliationPending.has(sessionId);
    };
    // Only an actual unresolved transient failure should arm the retry
    // backoff timer. Items left in the queue merely because their session
    // is reconciliation-held are NOT a failure — they're released by
    // `runReconciliation()` calling `drain()` again on its own, and must
    // not additionally arm the (queue-wide) retry timer, which would
    // otherwise re-freeze every session's drain behind an unrelated,
    // never-actually-attempted backoff.
    let hadTransientFailure = false;
    const processNext = async () => {
      if (disposed || !isEnabled) return;
      const currentIndex = state.queue.findIndex((item) => !isSessionHeld(item.operation));
      if (currentIndex === -1) return;
      const current = state.queue[currentIndex];
      // Defense in depth: re-validate immediately before execution, in case
      // in-memory state was mutated after load (or a validation bug slips
      // an invalid entry through readState).
      if (!isValidQueuedOperation(current.operation)) {
        removeItem(current);
        five9Logger.warn('five9.callQueueInvalidEntriesDropped', {
          count: 1,
          reason: 'invalidOperationShape',
          stage: 'execute'
        });
        await processNext();
        return;
      }
      try {
        await service.execute(current.operation);
        if (removeItem(current)) retryDelayMs = QUEUE_RETRY_INITIAL_DELAY_MS;
      } catch (error) {
        const currentIndex2 = state.queue.findIndex((item) => item.id === current.id);
        // Reconciliation may remove an in-flight POST after confirming that
        // the API already has it. In that case this result has nothing left
        // to settle and must never be applied to a successor.
        if (currentIndex2 === -1) {
          await processNext();
          return;
        }
        const isTerminal = !isRetryableFive9CallError(error);
        current.attempts = (current.attempts || 0) + 1;
        const isExhausted = current.attempts >= QUEUE_MAX_ATTEMPTS
          || (typeof current.enqueuedAt === 'number'
            && (now() - current.enqueuedAt) >= QUEUE_MAX_AGE_MS);
        if (isTerminal || isExhausted) {
          removeItem(current);
          retryDelayMs = QUEUE_RETRY_INITIAL_DELAY_MS;
          // No body/PII — only enough to diagnose which publish was
          // permanently dropped and why, so this never disappears silently.
          five9Logger.warn('five9.callPublishDropped', {
            method: current.operation.method,
            path: current.operation.path,
            status: error?.response?.status ?? null,
            attempts: current.attempts,
            reason: isTerminal ? 'terminalResponse' : 'retriesExhausted'
          });
        } else {
          hadTransientFailure = true;
          persist();
          return;
        }
      }
      await processNext();
    };
    draining = processNext().finally(() => {
      draining = null;
      if (!disposed && isEnabled && hadTransientFailure) scheduleRetry();
      if (!disposed && isEnabled && redrainRequested) {
        redrainRequested = false;
        drain();
      }
    });
    return draining;
  };

  const enqueue = (operations) => {
    if (disposed || !isEnabled) return;
    (operations || []).forEach((operation) => {
      const signature = signatureFor(operation);
      const exists = state.recent.includes(signature)
        || state.queue.some((item) => item.signature === signature);
      if (exists) return;
      itemSequence += 1;
      state.queue.push({
        id: `${now()}-${itemSequence}`,
        signature,
        enqueuedAt: now(),
        attempts: 0,
        operation
      });
    });
    persist();
    if (isEnabled) drain();
  };

  let runReconciliation;

  const removeQueuedCreateForSession = (sessionId) => {
    // Canonicalize to a single create per session before enqueueing a
    // 404-recovery POST: an already-queued POST for this session (e.g.
    // persisted from before this tab reloaded) would otherwise sit
    // alongside the fresh recovery POST below. Since
    // `mapFive9CallForReconciliation()` derives its own timestamps, the
    // two are frequently not byte-identical, so both would be sent —
    // relying on backend upsert idempotency to paper over an avoidable
    // duplicate write instead of avoiding it here. Deliberately does NOT
    // remember the removed item's signature: the fresh recovery POST must
    // still be allowed through even if it happens to match it exactly.
    const before = state.queue.length;
    state.queue = state.queue.filter((item) => !(
      item.operation.method === 'POST'
      && item.operation.body?.[FIVE9_CALL_API.sessionIdField] === sessionId
    ));
    if (state.queue.length !== before) persist();
  };

  const scheduleReconciliationRetry = () => {
    if (reconciliationRetryTimer
      || disposed
      || !isEnabled
      || !reconciliationPending.size) return;
    reconciliationRetryTimer = setTimeout(() => {
      reconciliationRetryTimer = null;
      runReconciliation();
    }, reconciliationRetryDelayMs);
    reconciliationRetryDelayMs = Math.min(
      reconciliationRetryDelayMs * 2,
      RECONCILIATION_RETRY_MAX_DELAY_MS
    );
  };

  runReconciliation = () => {
    if (disposed
      || !isEnabled
      || reconciliationRunning
      || reconciliationRetryTimer
      || !reconciliationPending.size) {
      return reconciliationRunning || Promise.resolve();
    }
    const entries = Array.from(reconciliationPending.entries());
    let hadRetryableFailure = false;
    reconciliationRunning = Promise.all(entries.map(async ([sessionId, pending]) => {
      try {
        await service.get(sessionId);
        if (disposed || !isEnabled) return;
        state.queue = state.queue.filter((item) => {
          const isReconciledCreate = item.operation.method === 'POST'
            && item.operation.body?.[FIVE9_CALL_API.sessionIdField] === sessionId;
          if (isReconciledCreate) remember(item.signature);
          return !isReconciledCreate;
        });
        persist();
        if (reconciliationPending.get(sessionId) === pending) {
          reconciliationPending.delete(sessionId);
        }
        reconciliationRetryDelayMs = RECONCILIATION_RETRY_INITIAL_DELAY_MS;
      } catch (error) {
        if (disposed || !isEnabled) return;
        if (error?.response?.status === 404) {
          removeQueuedCreateForSession(sessionId);
          enqueue(mapFive9CallForReconciliation(pending.call));
          if (reconciliationPending.get(sessionId) === pending) {
            reconciliationPending.delete(sessionId);
          }
          reconciliationRetryDelayMs = RECONCILIATION_RETRY_INITIAL_DELAY_MS;
        } else if (isRetryableFive9CallError(error)) {
          pending.attempts += 1;
          if (pending.attempts >= RECONCILIATION_MAX_ATTEMPTS) {
            if (reconciliationPending.get(sessionId) === pending) {
              reconciliationPending.delete(sessionId);
            }
            five9Logger.warn('five9.callReconciliationDropped', {
              status: error?.response?.status ?? null,
              attempts: pending.attempts,
              reason: 'retriesExhausted'
            });
          } else {
            hadRetryableFailure = true;
            five9Logger.warn('five9.callReconciliationRetry', {
              status: error?.response?.status ?? null,
              attempts: pending.attempts,
              reason: 'transientFailure'
            });
          }
        } else {
          if (reconciliationPending.get(sessionId) === pending) {
            reconciliationPending.delete(sessionId);
          }
          five9Logger.warn('five9.callReconciliationDropped', {
            status: error?.response?.status ?? null,
            attempts: pending.attempts + 1,
            reason: 'terminalResponse'
          });
        }
      }
    })).finally(() => {
      reconciliationRunning = null;
      if (disposed || !isEnabled) return undefined;
      if (!reconciliationPending.size) {
        return drain();
      }
      if (hadRetryableFailure) {
        scheduleReconciliationRetry();
      } else {
        runReconciliation();
      }
      return undefined;
    });
    return reconciliationRunning;
  };

  const reconcile = async (calls) => {
    if (disposed || !isEnabled) return;
    const unique = new Map();
    (calls || []).forEach((call) => {
      const sessionId = call?.five9SessionId || call?.sessionId;
      if (sessionId) unique.set(String(sessionId), call);
    });
    unique.forEach((call, sessionId) => {
      reconciliationSequence += 1;
      reconciliationPending.set(sessionId, {
        id: reconciliationSequence,
        call,
        attempts: reconciliationPending.get(sessionId)?.attempts || 0
      });
    });
    await runReconciliation();
    if (!disposed && isEnabled && !reconciliationPending.size) await drain();
  };

  const flushKeepalive = () => {
    if (disposed || !isEnabled) return;
    // Unlike drain(), a keepalive flush is fire-and-forget with no response
    // handling and no way to recover if requests race — the tab (and its
    // sessionStorage) may be gone before any response arrives. So it must
    // preserve the same two guarantees drain() gives a normal flush:
    //   (a) never send a write for a session still held by an unresolved
    //       reconciliation GET (the read-before-republish barrier), and
    //   (b) never let a later write for a session (e.g. a transcript PUT)
    //       race ahead of an earlier one still queued for that same
    //       session (e.g. the lifecycle PATCH creating/ending it) — a
    //       later op is skipped here and left for the next normal
    //       drain()/reconcile() (on reload or refocus) rather than sent
    //       out of order.
    const sentSessions = new Set();
    state.queue
      .filter((item) => isValidQueuedOperation(item.operation))
      .forEach((item) => {
        const sessionId = sessionIdForOperation(item.operation);
        if (sessionId != null) {
          if (reconciliationPending.has(sessionId) || sentSessions.has(sessionId)) return;
          sentSessions.add(sessionId);
        }
        service.keepalive(item.operation);
      });
  };

  // Coming back online is a cheap, reliable signal to redrain a queue that
  // was stuck behind a retryable failure while offline. Unlike a routine
  // reconcile, this justifies cancelling the pending backoff and retrying
  // now, so reset the delay and drain immediately.
  const handleOnline = () => {
    if (disposed || !isEnabled) return undefined;
    retryTimer = clearTimer(retryTimer);
    reconciliationRetryTimer = clearTimer(reconciliationRetryTimer);
    retryDelayMs = QUEUE_RETRY_INITIAL_DELAY_MS;
    reconciliationRetryDelayMs = RECONCILIATION_RETRY_INITIAL_DELAY_MS;
    if (reconciliationPending.size) return runReconciliation();
    return drain();
  };
  if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
    window.addEventListener('online', handleOnline);
  }

  const dispose = () => {
    disposed = true;
    isEnabled = false;
    retryTimer = clearTimer(retryTimer);
    reconciliationRetryTimer = clearTimer(reconciliationRetryTimer);
    reconciliationPending.clear();
    if (typeof window !== 'undefined' && typeof window.removeEventListener === 'function') {
      window.removeEventListener('online', handleOnline);
    }
  };

  return {
    enqueue,
    drain,
    reconcile,
    flushKeepalive,
    dispose,
    setEnabled(nextEnabled) {
      if (disposed) return;
      isEnabled = Boolean(nextEnabled);
      if (!isEnabled) {
        retryTimer = clearTimer(retryTimer);
        reconciliationRetryTimer = clearTimer(reconciliationRetryTimer);
      }
    },
    getDiagnostics: () => ({
      version: FIVE9_CALL_QUEUE_VERSION,
      pendingCount: state.queue.length,
      loadedPendingCount
    }),
    getStorageKey: () => key
  };
};

export { createFive9CallQueue };
export default createFive9CallQueue;
